import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, type Observable } from 'rxjs';

import { type DancerRole } from '../../app/models/DancerRole';
import { type DancerFilters, type DancerModel } from '../../models/Dancer';
import { type LeaderboardItem } from '../../models/Leaderboard';
import { type ParticipantExtendedDto } from '../../models/Participant';
import { type PoolModel } from '../../models/Pool';
import { type ResponseWithData } from '../../models/ResponseWithData';
import { type RoundModel } from '../../models/Round';
import { type RoundStatus } from '../../models/Status';
import { type ContestRole } from '../../models/User';
import { resolveHateoasUrl } from '../utils/hateoas.util';
import { buildHttpParams, type HttpOptions } from '../utils/http-params.util';
import { mapRawDancer, type RawParticipant } from '../utils/participant-mapper.util';

import { ApiService } from './api.service';

type ParticipantApiResponse = RawParticipant;

@Injectable({
  providedIn: 'root',
})
export class RoundApiService extends ApiService<RoundModel> {
  constructor() {
    super('rounds');
  }

  updateStatus(
    roundId: string,
    status: RoundStatus,
    skipStatusCheck = false,
  ): Observable<RoundModel> {
    let params = new HttpParams();
    if (skipStatusCheck) {
      params = params.set('skipStatusCheck', 'true');
    }
    return this.http
      .patch<
        ResponseWithData<RoundModel>
      >(`${this.url}/${this.apiEndpoint}/${roundId}/status`, { status }, { params })
      .pipe(map((res) => res.data));
  }

  getParticipantsOfRound(
    roundOrId: RoundModel | string,
    options?: {
      filters?: {
        contestRole?: ContestRole;
        /** Repeat the param server-side (?dancerRole=A&dancerRole=B) to match any of them. */
        dancerRole?: DancerRole[] | null;
      };
    },
  ): Observable<ParticipantExtendedDto[]> {
    const params = buildHttpParams(options as HttpOptions);

    const url = resolveHateoasUrl(roundOrId, `${this.url}/${this.apiEndpoint}`, 'participants');

    return this.http
      .get<ResponseWithData<ParticipantExtendedDto[]>>(url, { params })
      .pipe(map((res) => res.data));
  }

  getPoolListOfRound(roundOrId: RoundModel | string): Observable<PoolModel[]> {
    const url = resolveHateoasUrl(roundOrId, `${this.url}/${this.apiEndpoint}`, 'pools');

    return this.http.get<ResponseWithData<PoolModel[]>>(url).pipe(map((res) => res.data));
  }

  getRoundLeaderboard(roundId: string): Observable<LeaderboardItem[]> {
    return this.http
      .get<
        ResponseWithData<LeaderboardItem[]>
      >(`${this.url}/${this.apiEndpoint}/${roundId}/leaderboard`)
      .pipe(map((res) => res.data));
  }

  getUserListOfRound(
    roundOrId: RoundModel | string,
    options?: { filters?: DancerFilters },
  ): Observable<DancerModel[]> {
    const params = buildHttpParams(options as HttpOptions);

    const url = resolveHateoasUrl(roundOrId, `${this.url}/${this.apiEndpoint}`, 'participants');

    return this.http
      .get<ResponseWithData<ParticipantApiResponse[]>>(url, {
        params,
      })
      .pipe(map((res) => res.data.map((p) => mapRawDancer(p))));
  }

  addPoolToRound(roundId: string, poolId: string): Observable<unknown> {
    return this.http.post(`${this.url}/${this.apiEndpoint}/${roundId}/pools/${poolId}`, {});
  }

  addPoolsToRound(
    roundId: string,
    poolIds: string[],
    skipStatusCheck = false,
  ): Observable<unknown> {
    let params = new HttpParams();
    if (skipStatusCheck) {
      params = params.set('skipStatusCheck', 'true');
    }
    return this.http.post(
      `${this.url}/${this.apiEndpoint}/${roundId}/pools`,
      {
        poolIds: poolIds,
      },
      { params },
    );
  }

  removePoolFromRound(roundId: string, poolId: string): Observable<unknown> {
    return this.http.delete(`${this.url}/${this.apiEndpoint}/${roundId}/pools/${poolId}`);
  }

  removePoolsFromRound(roundId: string, poolIds: string[]): Observable<unknown> {
    return this.http.delete(`${this.url}/${this.apiEndpoint}/${roundId}/pools`, {
      body: { poolIds: poolIds },
    });
  }
}
